import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ProjektanfrageButton from '../../components/ProjectRequest/Projektanfrage';
import './HomeHeroSection.css';

/**
 * Home hero: light layout with an interactive dot-grid backdrop.
 * The grid drifts gently, a slow diagonal shimmer keeps it alive and the
 * pointer brightens nearby dots. A CSS mask fades the dots away from the
 * headline (and toward the edges), so the text always stays readable.
 * The animation pauses when the hero is off-screen or the tab is hidden.
 */
const HomeHeroSection = () => {
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const section = sectionRef.current;
    if (!canvas || !section) return undefined;

    const ctx = canvas.getContext('2d');
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let width = 0;
    let height = 0;
    let dots = [];
    let gap = 36;
    let influence = 140;

    // Pointer position is smoothed each frame for a fluid feel.
    const pointer = { x: -1e4, y: -1e4, targetX: -1e4, targetY: -1e4 };

    const buildDots = () => {
      dots = [];
      const cols = Math.ceil(width / gap) + 1;
      const rows = Math.ceil(height / gap) + 1;
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          dots.push({ ox: i * gap, oy: j * gap, x: i * gap, y: j * gap, phase: (i * 7 + j * 13) % 20 });
        }
      }
    };

    const resize = () => {
      const rect = section.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const mobile = width < 720;
      gap = mobile ? 30 : 36;
      influence = mobile ? 0 : 140; // no pointer effect on touch layouts
      buildDots();
    };

    const smoothstep = (f) => f * f * (3 - 2 * f);

    const draw = (now) => {
      const time = now * 0.001;
      ctx.clearRect(0, 0, width, height);

      pointer.x += (pointer.targetX - pointer.x) * 0.12;
      pointer.y += (pointer.targetY - pointer.y) * 0.12;

      for (let k = 0; k < dots.length; k++) {
        const d = dots[k];

        // Gentle organic drift
        const targetX = d.ox + Math.sin(time * 0.5 + d.phase) * 1.6;
        const targetY = d.oy + Math.cos(time * 0.42 + d.phase * 1.3) * 1.6;

        // Slow diagonal shimmer traveling across the grid
        const shimmer = Math.sin(time * 0.7 - (d.ox + d.oy) * 0.005);
        let radius = 1.5 + (shimmer + 1) * 0.25;
        let alpha = 0.13 + (shimmer + 1) * 0.045;

        // Pointer proximity: brighten and push slightly
        const dx = d.ox - pointer.x;
        const dy = d.oy - pointer.y;
        const dist = Math.hypot(dx, dy);
        if (influence > 0 && dist < influence) {
          const f = smoothstep(1 - dist / influence);
          const angle = Math.atan2(dy, dx);
          d.x += (targetX + Math.cos(angle) * f * 8 - d.x) * 0.15;
          d.y += (targetY + Math.sin(angle) * f * 8 - d.y) * 0.15;
          radius += f * 1.6;
          alpha = Math.min(0.85, alpha + f * 0.5);
        } else {
          d.x += (targetX - d.x) * 0.08;
          d.y += (targetY - d.y) * 0.08;
        }

        ctx.beginPath();
        ctx.arc(d.x, d.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(43, 168, 74, ${alpha})`; /* --accent */
        ctx.fill();
        // ctx.beginPath();
        // ctx.arc(d.x, d.y, radius, 0, Math.PI * 2);
        // ctx.fillStyle = `rgba(43, 168, 74, ${alpha})`;
        // ctx.fill();
      }
    };

    let frameId = null;
    let isVisible = true;

    const loop = (now) => {
      draw(now);
      frameId = requestAnimationFrame(loop);
    };

    const start = () => {
      if (frameId === null && !prefersReduced) frameId = requestAnimationFrame(loop);
    };
    const stop = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }
    };

    // Pointer tracking on the section, so text areas steer the grid too;
    // the canvas itself stays pointer-events: none (never blocks clicks).
    const onMouseMove = (e) => {
      const rect = section.getBoundingClientRect();
      pointer.targetX = e.clientX - rect.left;
      pointer.targetY = e.clientY - rect.top;
    };
    const onMouseLeave = () => {
      pointer.targetX = -1e4;
      pointer.targetY = -1e4;
    };

    // Pause work while the hero is off-screen or the tab is hidden
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      isVisible = entry.isIntersecting;
      if (isVisible && !document.hidden) start();
      else stop();
    });
    const onVisibilityChange = () => {
      if (document.hidden || !isVisible) stop();
      else start();
    };

    const resizeObserver = new ResizeObserver(() => {
      resize();
      if (prefersReduced) draw(performance.now());
    });

    resize();
    resizeObserver.observe(section);
    intersectionObserver.observe(section);
    document.addEventListener('visibilitychange', onVisibilityChange);
    section.addEventListener('mousemove', onMouseMove);
    section.addEventListener('mouseleave', onMouseLeave);

    if (prefersReduced) draw(performance.now());
    else start();

    return () => {
      stop();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      section.removeEventListener('mousemove', onMouseMove);
      section.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  return (
    <section className="home-hero-section" ref={sectionRef}>
      <canvas ref={canvasRef} className="home-hero-dots" aria-hidden="true" />

      <div className="home-hero-container">
        <span className="home-hero-badge">
          <span className="home-hero-badge-dot" aria-hidden="true" />
          Digitalagentur · Köln / Bonn
        </span>

        <h1 className="home-hero-title">
          Wir bauen digitale{' '}
          <br />
          Produkte, die
          <span className="highlight-green"> Wirkung zeigen.</span>
        </h1>

        <p className="home-hero-subtitle">
          Websites, Apps und digitale Erlebnisse für Industrie und Mittelstand –
          durchdacht konzipiert, sauber umgesetzt und messbar erfolgreich.
        </p>

        <div className="home-hero-buttons">
          <ProjektanfrageButton />
          <Link to="/case-studies" className="home-hero-btn home-hero-btn-secondary">
            Unsere Arbeiten &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomeHeroSection;
